import React, { useState, useRef, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import SignatureSidebar, { SignerOption } from "./utils/SignatureSidebar";
import PDFDropZone from "./utils/PDFDropZone";
import { FieldData } from "./utils/DraggableResizableField";
import { useNotification } from "../../../context/NotificationContext";

interface PrepareSignatureProps {
  pdfUrl: string;
  onClose: () => void;
  onReadyForSignature: (fields: FieldData[], dimensions: { width: number; height: number }) => Promise<void>;
  readyButtonLabel?: string;
  /**
   * Preserve previously-placed fields when re-opening the editor.
   * Without this, closing then re-opening wipes the user's work.
   */
  initialFields?: FieldData[];
  initialDimensions?: { width: number; height: number };
  /**
   * When provided, switches the editor into the "Bon pour accord" inline
   * flow: a signer picker replaces the generic Text/Date/Tampon palette,
   * and every placed field gets tagged with the active signer's id.
   */
  signers?: SignerOption[];
  /**
   * Inline mode: called when the user edits a signer's initials from the
   * sidebar input. Lets the parent persist the change.
   */
  onSignerInitialsChange?: (signerId: string, initials: string) => void;
}

/**
 * PrepareSignature Component
 *
 * Used to place signature fields on a PDF before preparing it for signature.
 * User drags and drops Signature, Text, Date, and Tampon fields onto the PDF.
 *
 * Usage:
 * ```tsx
 * // Option 1: Prepare via backend
 * <PrepareSignature
 *   pdfUrl={pdfFileUrl}
 *   onClose={() => setOpen(false)}
 *   readyButtonLabel="Prêt pour signature"
 *   onReadyForSignature={async (fields, dimensions) => {
 *     const response = await prepareSignatureAPI(fields, dimensions);
 *     setPreparedPdfUrl(response.pdf_url);
 *   }}
 * />
 *
 * // Option 2: Open signature modal directly
 * <PrepareSignature
 *   pdfUrl={pdfFileUrl}
 *   onClose={() => setOpen(false)}
 *   readyButtonLabel="Signer maintenant"
 *   onReadyForSignature={async (fields, dimensions) => {
 *     setSignatureFields(fields);
 *     setSignatureDimensions(dimensions);
 *     openFillSignatureModal();
 *   }}
 * />
 * ```
 */
const PrepareSignature: React.FC<PrepareSignatureProps> = ({
  pdfUrl,
  onClose,
  onReadyForSignature,
  readyButtonLabel = "Prêt pour signature",
  initialFields,
  initialDimensions,
  signers,
  onSignerInitialsChange,
}) => {
  const { showNotification } = useNotification();
  const [signFields, setSignFields] = useState<FieldData[]>(initialFields ?? []);
  const [pdfDimensions, setPdfDimensions] = useState(
    initialDimensions ?? { width: 1190.56, height: 1683.78 },
  );
  const [loadingData, setLoadingData] = useState(false);
  const inlineMode = !!signers && signers.length > 0;
  const [activeSignerId, setActiveSignerId] = useState<string | undefined>(
    signers?.[0]?.id,
  );
  // Keep the active signer aligned with the prop if it changes (e.g. signer
  // removed upstream).
  useEffect(() => {
    if (!signers || signers.length === 0) return;
    if (!activeSignerId || !signers.find((s) => s.id === activeSignerId)) {
      setActiveSignerId(signers[0].id);
    }
  }, [signers, activeSignerId]);

  // Start the id counter past any pre-seeded field ids so new additions
  // don't collide with the ones we reloaded.
  const fieldIdCounter = useRef(
    initialFields && initialFields.length > 0
      ? Math.max(...initialFields.map((f) => f.id)) + 1
      : 0,
  );

  // Visual zoom for the placement viewer. Field coordinates remain in the
  // canonical (unzoomed) space — DraggableResizableField scales position
  // and size by `zoom` for rendering, and divides drag/resize callbacks
  // back by `zoom` so stored data and backend math don't shift.
  const [zoom, setZoom] = useState(1);
  const ZOOM_STEP = 0.2;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2.5;

  const addField = (
    type: string,
    name: string | null = null,
    top: number = 100,
    left: number = 100,
    width: number = 500,
    height: number = 120,
    page: number = 1,
    _target?: string,
    signerId?: string,
  ) => {
    // In inline mode the "Bon pour accord" block has its own default
    // aspect ratio (roughly 3:2) so the three stacked rows fit nicely.
    const effectiveWidth = inlineMode ? 260 : width;
    const effectiveHeight = inlineMode ? 140 : height;

    // Only "Signature" typed fields participate in inline mode. If the
    // dragged item had no signerId but inlineMode is on, fall back to the
    // currently-active signer so the field isn't orphaned.
    const effectiveSignerId =
      inlineMode && type === "Signature"
        ? signerId ?? activeSignerId
        : signerId;

    const resolvedSignerName = inlineMode && effectiveSignerId
      ? signers!.find((s) => s.id === effectiveSignerId)?.name || ""
      : "";

    const newField: FieldData = {
      id: fieldIdCounter.current,
      type,
      name: inlineMode ? resolvedSignerName : name || type,
      width: effectiveWidth,
      height: effectiveHeight,
      top,
      left,
      page,
      signer_id: effectiveSignerId,
    };
    fieldIdCounter.current += 1;
    setSignFields((prev) => [...prev, newField]);
  };

  const handleUpdateFields = (
    id: number,
    type: string,
    name: string | null = null,
    top: number = 100,
    left: number = 100,
    width: number = 150,
    height: number = 80,
    page: number = 1,
  ) => {
    const updateName = name || type;
    console.log("x coord ", left)
    setSignFields((prevFields) =>
      prevFields.map((field) =>
        field.id === id
          ? {
              ...field,
              type: type,
              name: updateName,
              width: width,
              height: height,
              top: top,
              left: left,
              page: page,
            }
          : field
      )
    );
  };

  const handleFieldDelete = (id: number) => {
    setSignFields((prevFields) => prevFields.filter((field) => field.id !== id));
  };

  const handleFieldResize = (id: number, width: number, height: number) => {
    setSignFields((prevFields) =>
      prevFields.map((field) => (field.id === id ? { ...field, width, height } : field))
    );
  };

  const handleReadyForSignature = async () => {
    if (signFields.length === 0) {
      showNotification({ message: "Veuillez ajouter au moins un champ avant de continuer", type: "error" });
      return;
    }

    if (inlineMode && signers) {
      const missing = signers.filter(
        (s) => !signFields.some((f) => f.signer_id === s.id),
      );
      if (missing.length > 0) {
        showNotification({
          message: `Signataire(s) sans champ : ${missing.map((m) => m.name).join(", ")}`,
          type: "error",
        });
        return;
      }
    }

    setLoadingData(true);
    try {
      if (onReadyForSignature) {
        await onReadyForSignature(signFields, pdfDimensions);
      }
    } catch (error) {
      console.error("Error processing signature preparation:", error);
      showNotification({ message: "Échec de la préparation", type: "error" });
    } finally {
      setLoadingData(false);
    }
  };

  return (
    <div className="h-full w-full rounded-lg bg-white overflow-hidden">
      <DndProvider backend={HTML5Backend}>
        <div className="flex h-full w-full overflow-hidden">
          <SignatureSidebar
            editorMode="sign"
            handleEditorMode={() => {}}
            onSign={handleReadyForSignature}
            onEdit={() => {}}
            loadingData={loadingData}
            onClose={onClose}
            fields={signFields}
            handleRemoveTextField={() => {}}
            setIsModalOpen={() => {}}
            customTextFields={[]}
            signButtonLabel={readyButtonLabel}
            signers={signers}
            activeSignerId={activeSignerId}
            onActiveSignerChange={setActiveSignerId}
            onFieldDelete={(id) => handleFieldDelete(id)}
            onSignerInitialsChange={onSignerInitialsChange}
          />

          <div className="flex flex-col flex-1 min-w-0">
            {/* Zoom toolbar — sticky at the top of the viewer column. */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 8,
                padding: "8px 12px",
                backgroundColor: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(4px)",
                borderBottom: "1px solid #e5e7eb",
                zIndex: 10,
              }}
            >
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                disabled={zoom <= ZOOM_MIN}
                className="px-3 py-1 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                aria-label="Réduire"
                title="Réduire"
              >
                −
              </button>
              <button
                type="button"
                onClick={() => setZoom(1)}
                className="px-3 py-1 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 text-sm tabular-nums"
                aria-label="Réinitialiser le zoom"
                title="Réinitialiser"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                disabled={zoom >= ZOOM_MAX}
                className="px-3 py-1 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                aria-label="Agrandir"
                title="Agrandir"
              >
                +
              </button>
            </div>

            <div className="flex-1 min-h-0 flex">
              <PDFDropZone
                pdfUrl={pdfUrl}
                editFields={[]}
                signFields={signFields}
                addField={addField}
                editorMode="sign"
                handleFieldDelete={(id) => handleFieldDelete(id)}
                handleFieldResize={(id, w, h) => handleFieldResize(id, w, h)}
                handleUpdateFields={handleUpdateFields}
                setPdfDimensions={setPdfDimensions}
                pdfDimensions={pdfDimensions}
                zoom={zoom}
              />
            </div>
          </div>
        </div>
      </DndProvider>
    </div>
  );
};

export default PrepareSignature;
